<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import InfoSheetButton from '@uiKit/components/InfoSheetButton.vue'
import type { OfferingInfoTile } from '../constants/offeringInfo'

// One figure on a property card. Value, caption and ⓘ explainer all come
// from the offeringInfo builders; no sheet → no icon.

interface Props {
  label: string
  tile: OfferingInfoTile
}

defineProps<Props>()
</script>

<template>
  <Box direction="col" gap="2" class="metric-tile">
    <!-- 12px label: 14px uppercase bold doesn't fit a half-width tile. Wraps
         rather than truncates on the narrowest cards so it's never cut off. -->
    <Box align="start" justify="between" gap="2">
      <Text
        type="caption"
        weight="bold"
        color="muted"
        transform="uppercase"
        class="metric-tile__label"
        style="letter-spacing: 0.3px; line-height: 16px"
      >
        {{ label }}
      </Text>
      <InfoSheetButton v-if="tile.sheet" :content="tile.sheet" :label="tile.sheet.title" />
    </Box>
    <!-- heading (16px) rather than 18px: a 7-digit AED price must fit a
         half-width tile at every grid breakpoint without truncating. -->
    <Text is="span" type="heading" truncate><CurrencyText :text="tile.value || '-'" /></Text>
    <Box v-if="tile.caption" align="center" gap="1">
      <PiIcon v-if="tile.isDiscountCaption" name="percentage" class="metric-tile__discount-icon" />
      <Text
        is="span"
        type="body"
        weight="medium"
        :color="tile.isDiscountCaption ? 'success' : 'muted'"
        truncate
      >
        <CurrencyText :text="tile.caption" />
      </Text>
    </Box>
  </Box>
</template>

<style scoped>
.metric-tile {
  min-width: 0;
  padding: 12px;
  border-radius: 16px;
  border: 1px solid var(--p-primary-100);
  background: var(--p-surface-0);
}

.metric-tile__label {
  min-width: 0;
  overflow-wrap: anywhere;
}

.metric-tile__discount-icon {
  font-size: 12px;
  color: var(--p-success-600);
}
</style>
